import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { ImageField } from "./_shared";

export function ContentPanel() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-content").then(({ data }) => setContent(data));
  }, []);

  if (!content) return <p>Loading…</p>;

  const update = (k, v) => setContent({ ...content, [k]: v });

  const updateInit = (idx, k, v) => {
    const ini = [...content.initiatives];
    ini[idx] = { ...ini[idx], [k]: v };
    setContent({ ...content, initiatives: ini });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/site-content", content);
      toast.success("Site content updated");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} data-testid="content-panel">
      <p className="eyebrow mb-2">Landing page</p>
      <h1 className="font-serif-display text-4xl mb-8" style={{ color: "var(--ser-primary-dark)" }}>
        Edit site content
      </h1>

      <div className="ser-card p-6 mb-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Hero section</h2>
        <div className="space-y-4">
          <div>
            <label className="ser-label">Eyebrow text</label>
            <input className="ser-input" value={content.hero_eyebrow}
              onChange={(e) => update("hero_eyebrow", e.target.value)} data-testid="hero-eyebrow-input" />
          </div>
          <div>
            <label className="ser-label">Title</label>
            <input className="ser-input" value={content.hero_title}
              onChange={(e) => update("hero_title", e.target.value)} data-testid="hero-title-input" />
          </div>
          <div>
            <label className="ser-label">Subtitle</label>
            <textarea rows={3} className="ser-input" value={content.hero_subtitle}
              onChange={(e) => update("hero_subtitle", e.target.value)} data-testid="hero-subtitle-input" />
          </div>
          <ImageField
            label="Hero image"
            value={content.hero_image}
            onChange={(v) => update("hero_image", v)}
            testIdPrefix="hero-image"
          />
        </div>
      </div>

      <div className="ser-card p-6 mb-6" data-testid="banner-section">
        <h2 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ser-primary-dark)" }}>Banner messages</h2>
        <p className="text-xs mb-5" style={{ color: "var(--ser-text-muted)" }}>
          Two full-width banners shown on the homepage. Use these for high-impact messaging or call-to-action announcements (e.g. conferences, campaigns).
        </p>
        <div className="space-y-4">
          <div>
            <label className="ser-label">Primary banner (green) — under hero</label>
            <textarea rows={3} className="ser-input" value={content.banner_text || ""}
              onChange={(e) => update("banner_text", e.target.value)} data-testid="banner-text-input" />
          </div>
          <div>
            <label className="ser-label">Secondary banner (teal) — between initiatives & intro</label>
            <textarea rows={3} className="ser-input" value={content.banner2_text || ""}
              onChange={(e) => update("banner2_text", e.target.value)} data-testid="banner2-text-input" />
          </div>
        </div>
      </div>

      <div className="ser-card p-6 mb-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Intro section</h2>
        <div className="space-y-4">
          <div>
            <label className="ser-label">Intro title</label>
            <input className="ser-input" value={content.intro_title}
              onChange={(e) => update("intro_title", e.target.value)} data-testid="intro-title-input" />
          </div>
          <div>
            <label className="ser-label">Intro body</label>
            <textarea rows={5} className="ser-input" value={content.intro_body}
              onChange={(e) => update("intro_body", e.target.value)} data-testid="intro-body-input" />
          </div>
          <ImageField
            label="Intro image"
            value={content.intro_image}
            onChange={(v) => update("intro_image", v)}
            testIdPrefix="intro-image"
          />
        </div>
      </div>

      <div className="ser-card p-6 mb-6">
        <h2 className="font-serif-display text-2xl mb-5" style={{ color: "var(--ser-primary-dark)" }}>Initiatives</h2>
        <div className="space-y-6">
          {content.initiatives.map((i, idx) => (
            <div key={i.id} className="p-4 rounded-lg" style={{ backgroundColor: "var(--ser-section-alt, #f5f5f0)" }}
              data-testid={`init-editor-${i.id}`}>
              <p className="eyebrow mb-3">Initiative {idx + 1}</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ser-label">Title</label>
                  <input className="ser-input" value={i.title}
                    onChange={(e) => updateInit(idx, "title", e.target.value)} />
                </div>
                <div>
                  <label className="ser-label">Tag</label>
                  <input className="ser-input" value={i.tag}
                    onChange={(e) => updateInit(idx, "tag", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="ser-label">Description</label>
                  <textarea rows={2} className="ser-input" value={i.description}
                    onChange={(e) => updateInit(idx, "description", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <ImageField
                    label="Image"
                    value={i.image_url}
                    onChange={(v) => updateInit(idx, "image_url", v)}
                    testIdPrefix={`init-${i.id}-image`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" disabled={saving} className="btn-ser-primary" data-testid="save-content-btn">
        {saving ? "Saving…" : "Save changes"}
      </button>
    </form>
  );
}
